import type { Language, Translation } from '../index';

// Définir la structure de traduction sans auto-référence
export const fr = {
  common: {
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    add: 'Ajouter',
    loading: 'Chargement...',
    error: 'Une erreur est survenue',
    success: 'Succès'
  },
  cookies: {
    notice: 'Nous utilisons des cookies pour améliorer votre expérience',
    description: 'Nous utilisons des cookies essentiels pour faire fonctionner ce site. Vous pouvez autoriser des cookies supplémentaires pour personnaliser votre expérience.',
    essential: 'Uniquement essentiels',
    accept: 'Tout accepter'
  },
  auth: {
    signIn: 'Se connecter',
    signUp: "S'inscrire",
    signOut: 'Se déconnecter',
    email: 'Email',
    password: 'Mot de passe',
    forgotPassword: 'Mot de passe oublié ?',
    resetPassword: 'Réinitialiser le mot de passe'
  },
  chat: {
    inputPlaceholder: 'Écrivez votre message...',
    welcomeMessage: 'Bonjour ! Comment puis-je vous aider ?',
    sendMessage: 'Envoyer',
    typing: 'En train d\'écrire...'
  },
  settings: {
    general: 'Général',
    design: 'Design',
    ai: 'Paramètres IA',
    miniApps: 'Mini Apps',
    profile: 'Profil',
    widget: 'Widget',
    orderTracking: {
      quickActionText: 'Suivre ma commande',
      promptMessage: 'Veuillez entrer votre numéro de commande pour suivre votre commande :',
      errorMessage: 'Désolé, nous n\'avons pas trouvé cette commande. Veuillez vérifier le numéro de commande et réessayer.',
      button: 'Suivre ma commande',
      orderStatus: {
        pending: 'En attente',
        processing: 'En traitement',
        shipped: 'Expédiée',
        delivered: 'Livrée',
        cancelled: 'Annulée'
      }
    }
  },
  miniApps: {
    products: {
      title: 'Produits',
      description: 'Parcourir nos produits',
      price: 'Prix',
      stock: 'Stock'
    },
    calendar: {
      title: 'Calendrier',
      description: 'Prendre rendez-vous',
      bookAppointment: 'Réserver'
    },
    services: {
      title: 'Services',
      description: 'Nos services',
      learnMore: 'En savoir plus'
    },
    gallery: {
      title: 'Galerie',
      description: 'Voir notre galerie'
    },
    realestate: {
      title: 'Immobilier',
      description: 'Parcourir nos biens',
      price: 'Prix',
      surface: 'Surface',
      rooms: 'Pièces',
      location: 'Localisation',
      type: 'Type de bien',
      status: 'Statut',
      features: 'Caractéristiques'
    }
  },
  pages: {
    home: {
      hero: {
        title: "L'Intelligence Artificielle au",
        subtitle: "service de vos utilisateurs",
        description: "Découvrez la nouvelle génération de chatbots propulsés par l'IA. Profitez d'une qualification et d'une captation de leads fluides et sans faille grâce à nos outils uniques sur le marché.",
        cta: {
          primary: 'Démarrer gratuitement',
          secondary: 'Voir les tarifs',
          dashboard: 'Accéder à mon Dashboard'
        }
      },
      demo: {
        title: 'Découvrez notre Démo',
        description: 'Testez notre chatbot en direct et découvrez toutes ses fonctionnalités'
      },
      leadDetection: {
        title: 'Détection et captation',
        highlight: 'automatique',
        suffix: 'des leads',
        description: 'Captez un maximum de données et comprenez en profondeur les besoins de vos visiteurs grâce à nos outils d\'analyse avancés.',
        features: {
          emails: {
            title: 'Détection des emails',
            description: 'Capture automatique des adresses email mentionnées dans les conversations'
          },
          phones: {
            title: 'Détection des téléphones',
            description: 'Reconnaissance intelligente des numéros de téléphone dans les échanges'
          },
          notifications: {
            title: 'Notifications instantanées',
            description: 'Soyez alerté en temps réel de chaque nouveau lead détecté'
          },
          crm: {
            title: 'Intégrations CRM',
            description: 'Synchronisation automatique avec Salesforce, HubSpot et plus encore'
          }
        }
      },
      design: {
        title: 'Ultra',
        highlight: 'personnalisation',
        suffix: 'du design de votre bot !',
        description: 'Créez un chatbot unique qui reflète parfaitement votre identité visuelle. Notre interface intuitive vous permet de personnaliser chaque aspect de l\'apparence.',
        features: {
          colors: 'Dégradés de couleurs personnalisables',
          messages: 'Personnalisation des messages et textes',
          themes: 'Thèmes clairs et sombres'
        }
      },
      ai: {
        title: 'Un paramétrage',
        highlight: 'IA fluide',
        suffix: 'et structuré',
        description: 'Configurez votre assistant IA en quelques clics. Notre interface intuitive vous guide à travers chaque étape pour une efficacité optimale.',
        features: {
          personalization: 'Personnalisation avancée de l\'IA',
          security: 'Sécurité et confidentialité garanties',
          multilingual: 'Support multilingue'
        }
      },
      miniApps: {
        title: 'Des',
        highlight: 'Mini Apps',
        suffix: 'pour tous vos besoins !',
        description: 'Offrez une expérience unique et interactive à vos clients. Plus besoin de changer de page, tout est intégré directement dans le chat !',
        apps: {
          calendar: {
            title: 'Calendrier',
            description: 'Prise de rendez-vous intégrée'
          },
          products: {
            title: 'Produits',
            description: 'Catalogue interactif'
          },
          services: {
            title: 'Services',
            description: 'Présentation dynamique'
          },
          gallery: {
            title: 'Galerie',
            description: 'Photos et vidéos'
          }
        }
      },
      multilingual: {
        title: 'Support',
        highlight: 'Multilingue',
        suffix: 'Intégré',
        description: 'Communiquez avec vos clients dans leur langue. Notre chatbot s\'adapte automatiquement à la langue de vos visiteurs.',
        features: {
          translation: 'Traduction complète',
          choice: 'Donnez le choix à vos utilisateurs',
          configuration: 'Configuration Simple'
        }
      },
      analytics: {
        title: 'Apprenez sur vos',
        highlight: 'utilisateurs',
        description: 'Captez un maximum de données et comprenez en profondeur les besoins de vos visiteurs.',
        features: {
          stats: {
            title: 'Statistiques avancées',
            description: 'Analysez en détail le comportement de vos visiteurs et optimisez votre stratégie.'
          },
          segments: {
            title: 'Segmentation utilisateurs',
            description: 'Identifiez vos segments d\'utilisateurs les plus pertinents.'
          }
        }
      },
      linktree: {
        title: 'Fini les',
        highlight: 'LinkTree',
        suffix: '!',
        description: 'Captez plus de leads, faites parler votre marque ou votre personal branding, littéralement ! Dynamisez votre site et retenez vos utilisateurs.',
        features: {
          sharing: {
            title: 'Partage interactif',
            description: 'Partagez vos liens de manière interactive et engageante avec vos visiteurs.'
          },
          conversion: {
            title: 'Conversion optimisée',
            description: 'Augmentez vos taux de conversion grâce à une expérience utilisateur unique.'
          },
          analytics: {
            title: 'Analytics détaillés',
            description: 'Suivez et analysez les performances de vos liens partagés.'
          }
        }
      },
      integrations: {
        title: 'Puissantes',
        highlight: 'Intégrations',
        suffix: 'à portée de main',
        badge: 'Plateforme Tout-en-Un',
        subtitle: 'à votre service',
        description: 'Connectez vos outils et services préférés en toute simplicité. Notre plateforme s\'intègre avec les solutions d\'entreprise les plus populaires.',
        features: {
          crm: {
            title: 'Intégration CRM',
            description: 'Synchronisez automatiquement vos leads avec Salesforce, HubSpot et plus'
          },
          analytics: {
            title: 'Intégration Analytics',
            description: 'Suivez les conversions et le comportement des utilisateurs avec les outils d\'analyse populaires'
          },
          marketing: {
            title: 'Intégration Outils Marketing',
            description: 'Connectez-vous à vos plateformes d\'email marketing et d\'automatisation'
          },
          automation: {
            title: 'Automatisation de Flux',
            description: 'Créez des automatisations puissantes avec Zapier, Make et d\'autres outils de workflow'
          },
          web: {
            title: 'Intégration Web',
            description: 'Intégrez facilement avec votre site web et vos applications web'
          }
        }
      },
      ecommerce: {
        title: 'Solution',
        highlight: 'E-commerce',
        suffix: 'complète',
        description: 'Gérez votre boutique en ligne sans effort. De la gestion des produits au suivi des commandes, tout ce dont vous avez besoin au même endroit.',
        features: {
          products: {
            title: 'Gestion des Produits',
            description: 'Gérez et synchronisez facilement votre catalogue de produits'
          },
          orders: {
            title: 'Suivi des Commandes',
            description: 'Suivi des commandes en temps réel'
          },
          cart: {
            title: 'Récupération de Panier',
            description: 'Récupération automatisée des paniers abandonnés'
          },
          promotions: {
            title: 'Promotions',
            description: 'Créez et gérez des campagnes promotionnelles'
          }
        }
      }
    },
    pricing: {
      title: 'Choisissez votre plan',
      subtitle: 'Botmaker',
      description: 'Des forfaits adaptés à tous vos besoins',
      monthly: 'Mensuel',
      yearly: 'Annuel',
      free: 'Gratuit',
      starter: 'Starter',
      professional: 'Professionnel',
      enterprise: 'Entreprise',
      features: {
        ai: 'Paramètres IA',
        design: 'Paramètres Design',
        widget: "Codes d'intégrations",
        integrations: 'Toutes les mini-apps',
        gallery: 'Galerie photos',
        conversations: 'Conversations',
        stats: 'Statistiques'
      },
      billing_info: 'Tous les prix sont en euros HT. La facturation est',
      conversations: {
        title: 'Comment fonctionnent les conversations ?',
        description: 'Une conversation démarre au premier message de l\'utilisateur et se termine après une période d\'inactivité. Peu importe le nombre d\'échanges (2 ou 50 messages), cela compte comme une seule conversation. Chaque forfait inclut un nombre défini de conversations par mois, vous permettant d\'interagir de manière approfondie avec vos visiteurs.'
      },
      custom: {
        title: 'Besoin d\'un forfait sur mesure ?',
        description: 'Vous souhaitez un volume de conversations plus important ou un chatbot 100% personnalisé ? Notre équipe est à votre disposition pour étudier vos besoins et vous proposer une solution adaptée.',
        cta: 'Prendre rendez-vous'
      }
    }
  },
  footer: {
    product: {
      title: 'Produit',
      features: 'Fonctionnalités',
      pricing: 'Tarifs',
      guide: 'Guide',
      miniApps: 'Mini Apps',
      integrations: 'Intégrations'
    },
    resources: {
      title: 'Ressources',
      documentation: 'Documentation',
      blog: 'Blog'
    },
    company: {
      title: 'Entreprise',
      about: 'À propos',
      contact: 'Contact',
      careers: 'Carrières',
      partners: 'Partenaires'
    },
    legal: {
      title: 'Légal',
      terms: 'CGU',
      privacy: 'Politique de confidentialité',
      cookies: 'Cookies',
      mentions: 'Mentions légales'
    },
    copyright: '© 2025 Botmaker Agency. Tous droits réservés.'
  }
};